import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, List, Eye, EyeOff, Wifi, WifiOff, Search, MessageCircle, Shield, Zap, Hash } from 'lucide-react'
import { apiClient } from '@/lib/api'
import type {
  MonitorEventItem,
  MonitorEventsParams,
  MonitorConfig,
  MonitorStats,
  EscalationData,
  CorrelationSignalsResponse,
  MonitorLiveAircraft,
  MonitorLiveVessel,
  GpsJamJammingResponse,
  GpsJamSpoofingGeoJSON,
  PolymarketMarketsResponse,
  StockSummaryResponse,
  CloudflareOutagesResponse,
  MonitorNewsResponse,
  TelegramFeedResponse,
  TwitterFeedResponse,
  GammaIrradiatorsResponse,
  GammaIrradiatorFacility,
  MilitaryBasesResponse,
  MilitaryBase,
} from '@/lib/api'
import GlobeView, { type ArcData, type RingData, ARC_TYPE_COLORS } from '@/components/monitor/GlobeView'
import AircraftDetailPanel from '@/components/monitor/AircraftDetailPanel'
import EventFilters from '@/components/monitor/EventFilters'
import EventDetail from '@/components/monitor/EventDetail'
import FlightDetailCard from '@/components/monitor/FlightDetailCard'
import VesselDetailCard from '@/components/monitor/VesselDetailCard'
import GammaDetailCard from '@/components/monitor/GammaDetailCard'
import MilitaryBaseDetailCard from '@/components/monitor/MilitaryBaseDetailCard'
import StatsBar from '@/components/monitor/StatsBar'
import LayerToggles, { type LayerState, DEFAULT_LAYERS, getDomainsFromLayers, getEventTypesFromLayers } from '@/components/monitor/LayerToggles'
import EscalationMeter from '@/components/monitor/EscalationMeter'
import MonitorNewsPanel from '@/components/monitor/MonitorNewsPanel'
import AttackTicker from '@/components/monitor/AttackTicker'
import LoadingOverlay from '@/components/monitor/LoadingOverlay'
import CrisisMode from '@/components/monitor/CrisisMode'
import CountryIntelPanel from '@/components/monitor/CountryIntelPanel'
import EscalationDetailPanel from '@/components/monitor/EscalationDetailPanel'
import SanctionsPanel from '@/components/monitor/SanctionsPanel'
import EventMap3D from '@/components/monitor/EventMap3D'
import useMonitorWS from '@/components/monitor/useMonitorWS'
import MonitorSearch from '@/components/monitor/MonitorSearch'
import { getCountryLatLng, getRandomAttackerOrigin } from '@/components/monitor/countryCoords'
import { getSpreadEventCoordinates } from '@/components/monitor/eventCoordinateSpread'
import { SEOHead } from '@/components/SEOHead'

type LeftPanelSectionId = 'layers' | 'escalation' | 'signals' | 'filters' | 'news'
type FlashRing = RingData & { _expiresAt: number }
type LiveToast = { id: string; domain: string; severity: string; title: string; country: string | null; color: string; expiresAt: number }
type DdosAlert = { id: string; title: string; country: string | null; severity: string; ts: number; expiresAt: number }

function isDdosEvent(e: { event_type: string; title: string; metadata?: Record<string, any> }): boolean {
  if (e.event_type === 'ddos') return true
  const t = e.title.toLowerCase()
  if (t.includes('ddos') || t.includes('denial of service') || t.includes('dos attack')) return true
  const cat = (e.metadata?.category as string | undefined) ?? ''
  return cat.toLowerCase().includes('ddos')
}

const DOMAIN_FLASH_COLORS: Record<string, [number, number, number]> = {
  cyber:          [239, 68,  68 ],
  geopolitical:   [249, 115, 22 ],
  financial:      [234, 179, 8  ],
  maritime:       [59,  130, 246],
  natural:        [34,  197, 94 ],
  aviation:       [168, 85,  247],
  infrastructure: [6,   182, 212],
}

function flashColorForEvent(domain: string, severity: string): string {
  const [r, g, b] = DOMAIN_FLASH_COLORS[domain] ?? [148, 163, 184]
  const a = severity === 'critical' ? 0.9 : severity === 'high' ? 0.7 : 0.5
  return `rgba(${r},${g},${b},${a})`
}

function DisclaimerBanner({ onAccept }: { onAccept: () => void }) {
  return (
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
      <div className="max-w-lg w-full mx-4 bg-gray-950 border border-gray-800 rounded-xl p-8 shadow-2xl text-center space-y-6">
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="w-14 h-14 rounded-lg overflow-hidden shadow-lg shadow-green-500/50">
              <img src="/logo.jpeg" alt="Sycek OSINT" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -inset-1 bg-green-500/20 rounded-lg blur-sm animate-pulse" />
          </div>
          <div className="text-center">
            <h1 className="text-xl font-bold text-green-400 font-mono drop-shadow-[0_0_10px_rgba(34,197,94,0.6)]">Sycek</h1>
            <p className="text-xs text-green-500/70 font-mono">// next-gen cyber intel & osint</p>
          </div>
        </div>

        <div className="text-left space-y-4 text-[13px] text-gray-400 leading-relaxed">
          <div className="bg-gray-900/60 border border-gray-800/60 rounded-lg p-4 space-y-2">
            <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Data Handling Agreement</h3>
            <p>
              By proceeding, you acknowledge and agree that all intelligence data presented on this platform
              is intended solely for lawful, ethical, and authorized purposes. You agree to handle all
              information responsibly, in compliance with applicable laws and regulations, and to refrain
              from any use that could cause harm, violate individual privacy rights, or compromise
              operational security.
            </p>
          </div>

          <div className="bg-gray-900/60 border border-gray-800/60 rounded-lg p-4 space-y-2">
            <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Display Recommendation</h3>
            <p>
              This platform is optimized for desktop environments with a minimum resolution of 1440&times;900.
              For the best experience, please use a desktop or laptop browser in full-screen mode.
            </p>
          </div>
        </div>

        <button
          onClick={onAccept}
          className="w-full py-3 bg-green-600 hover:bg-green-500 text-white font-semibold text-sm rounded-lg transition-colors tracking-wide font-mono uppercase"
        >
          I Agree &mdash; Proceed to Dashboard
        </button>

        <p className="text-[10px] text-gray-700 font-mono">
          Unauthorized or unethical use of this platform is strictly prohibited.
        </p>
      </div>
    </div>
  )
}

export default function MonitorDashboard() {
  const [accepted, setAccepted] = useState(() => localStorage.getItem('sycek_monitor_consent') === '1')

  if (!accepted) {
    return (
      <DisclaimerBanner onAccept={() => {
        localStorage.setItem('sycek_monitor_consent', '1')
        setAccepted(true)
      }} />
    )
  }

  return <MonitorDashboardInner />
}

function MonitorDashboardInner() {
  const [layers, setLayers] = useState<LayerState>(DEFAULT_LAYERS)
  const [params, setParams] = useState<MonitorEventsParams>({
    page: 1,
    page_size: 300,
    severities: 'critical,high',
  })
  const [selectedEvent, setSelectedEvent] = useState<MonitorEventItem | null>(null)
  const [showList, setShowList] = useState(false)
  const [colorBy, setColorBy] = useState<'severity' | 'domain'>('domain')
  const [showLegend, setShowLegend] = useState(true)
  const [wsEnabled, setWsEnabled] = useState(true)
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [selectedAircraft, setSelectedAircraft] = useState<MonitorLiveAircraft | null>(null)
  const [selectedVessel, setSelectedVessel] = useState<MonitorLiveVessel | null>(null)
  const [selectedGamma, setSelectedGamma] = useState<GammaIrradiatorFacility | null>(null)
  const [selectedBase, setSelectedBase] = useState<MilitaryBase | null>(null)
  const [showEscalationDetail, setShowEscalationDetail] = useState(false)
  const [flashRings, setFlashRings] = useState<FlashRing[]>([])
  const [liveToasts, setLiveToasts] = useState<LiveToast[]>([])
  const [ddosAlerts, setDdosAlerts] = useState<DdosAlert[]>([])
  const seenLiveIds = useRef<Set<string>>(new Set())
  const liveInitialized = useRef(false)
  const eventsSeeded = useRef(false)
  const shownDdosIds = useRef<Set<string>>(new Set())
  const [leftPanelOrder, setLeftPanelOrder] = useState<LeftPanelSectionId[]>([
    'layers',
    'escalation',
    'signals',
    'filters',
    'news',
  ])
  const globeRef = useRef<any>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const domains = getDomainsFromLayers(layers)
    const eventTypes = getEventTypesFromLayers(layers)
    setParams((prev) => ({
      ...prev,
      domains,
      event_types: eventTypes,
      page: 1,
    }))
  }, [layers])

  useEffect(() => {
    if (severityFilter) {
      setParams((prev) => ({ ...prev, severities: severityFilter, page: 1 }))
      setShowList(true)
    } else {
      setParams((prev) => ({ ...prev, severities: 'critical,high', page: 1 }))
    }
  }, [severityFilter])

  const { data: config } = useQuery<MonitorConfig>({
    queryKey: ['monitor-config'],
    queryFn: () => apiClient.getMonitorConfig(),
    staleTime: 5 * 60 * 1000,
    retry: 3,
  })

  const { data: eventsData, isLoading: eventsLoading, isFetching: eventsFetching, refetch: refetchEvents } = useQuery({
    queryKey: ['monitor-events', params],
    queryFn: () => apiClient.getMonitorEvents(params),
    refetchInterval: 60_000,
    retry: 3,
  })

  const { data: stats, isLoading: statsLoading } = useQuery<MonitorStats>({
    queryKey: ['monitor-stats'],
    queryFn: () => apiClient.getMonitorStats(),
    refetchInterval: 60_000,
    retry: 3,
  })

  const { data: escalation, isLoading: escalationLoading } = useQuery<EscalationData>({
    queryKey: ['monitor-escalation'],
    queryFn: () => apiClient.getMonitorEscalation(),
    refetchInterval: 60_000,
    retry: 3,
  })

  const { data: correlationData } = useQuery<CorrelationSignalsResponse>({
    queryKey: ['monitor-correlation-signals'],
    queryFn: () => apiClient.getCorrelationSignals(24),
    refetchInterval: 120_000,
    retry: 2,
  })

  const { data: liveVesselsData } = useQuery({
    queryKey: ['monitor-live-vessels'],
    queryFn: () => apiClient.getMonitorLiveVessels(undefined, 200),
    refetchInterval: 90_000,
    retry: 2,
    enabled: layers.maritime,
  })

  const { data: liveFlightsData } = useQuery({
    queryKey: ['monitor-live-flights'],
    queryFn: () => apiClient.getMonitorLiveFlights(undefined, 300),
    refetchInterval: 60_000,
    retry: 2,
    enabled: layers.flights,
  })

  const gpsJamPeriod = useMemo(() => {
    const end = new Date()
    const start = new Date(end.getTime() - 24 * 60 * 60 * 1000)
    return {
      periodStart: start.toISOString().replace(/\.\d{3}Z$/, '.000Z'),
      periodEnd: end.toISOString().replace(/\.\d{3}Z$/, '.000Z'),
      startTime: start.toISOString().replace(/\.\d{3}Z$/, '.000Z'),
      endTime: end.toISOString().replace(/\.\d{3}Z$/, '.000Z'),
    }
  }, [])

  const { data: gpsJamJammingData } = useQuery<GpsJamJammingResponse>({
    queryKey: ['monitor-gpsjam-jamming', gpsJamPeriod.periodStart, gpsJamPeriod.periodEnd],
    queryFn: () => apiClient.getMonitorGpsJamming(gpsJamPeriod.periodStart, gpsJamPeriod.periodEnd),
    refetchInterval: 120_000,
    retry: 2,
    enabled: layers.gpsjam,
  })

  const { data: gpsJamSpoofingData } = useQuery<GpsJamSpoofingGeoJSON>({
    queryKey: ['monitor-gpsjam-spoofing', gpsJamPeriod.startTime, gpsJamPeriod.endTime],
    queryFn: () => apiClient.getMonitorGpsSpoofing(gpsJamPeriod.startTime, gpsJamPeriod.endTime),
    refetchInterval: 120_000,
    retry: 2,
    enabled: layers.gpsjam,
  })

  const { liveEvents, connected: wsConnected } = useMonitorWS(wsEnabled)

  const { data: polymarketData, isLoading: polymarketLoading } = useQuery<PolymarketMarketsResponse>({
    queryKey: ['monitor-polymarket'],
    queryFn: () => apiClient.getMonitorPolymarketMarkets(),
    refetchInterval: 5 * 60_000,
    staleTime: 4 * 60_000,
  })

  const { data: stockSummary, isLoading: stockSummaryLoading } = useQuery<StockSummaryResponse>({
    queryKey: ['monitor-stocks'],
    queryFn: () => apiClient.getMonitorStockSummary(),
    refetchInterval: 5 * 60_000,
    staleTime: 4 * 60_000,
  })

  const { data: cloudflareOutages } = useQuery<CloudflareOutagesResponse>({
    queryKey: ['monitor-cloudflare-outages'],
    queryFn: () => apiClient.getMonitorCloudflareOutages(),
    refetchInterval: 5 * 60_000,
    staleTime: 4 * 60_000,
  })

  const { data: newsData, isLoading: newsLoading, refetch: refetchNews } = useQuery<MonitorNewsResponse>({
    queryKey: ['monitor-news'],
    queryFn: () => apiClient.getMonitorNews(),
    refetchInterval: 5 * 60_000,
    staleTime: 4 * 60_000,
  })

  const { data: telegramFeed, isLoading: telegramFeedLoading, isError: telegramFeedError } = useQuery<TelegramFeedResponse>({
    queryKey: ['monitor-telegram-feed'],
    queryFn: () => apiClient.getMonitorTelegramFeed(25),
    refetchInterval: 30_000,
    staleTime: 20_000,
    retry: false,
  })

  const { data: twitterFeed, isLoading: twitterFeedLoading, isError: twitterFeedError } = useQuery<TwitterFeedResponse>({
    queryKey: ['monitor-twitter-feed'],
    queryFn: () => apiClient.getMonitorTwitterFeed(20),
    refetchInterval: 45_000,
    staleTime: 30_000,
    retry: false,
  })

  const { data: gammaData } = useQuery<GammaIrradiatorsResponse>({
    queryKey: ['monitor-gamma-irradiators'],
    queryFn: () => apiClient.getGammaIrradiators(),
    enabled: layers.gamma === true,
    staleTime: 24 * 60_60_1000,
  })

  const { data: basesData } = useQuery<MilitaryBasesResponse>({
    queryKey: ['monitor-military-bases'],
    queryFn: () => apiClient.getMilitaryBases(),
    enabled: layers.bases === true,
    staleTime: 24 * 60_60_1000,
  })

  const events = useMemo(() => {
    const restEvents = eventsData?.events || []
    const raw = liveEvents.length === 0
      ? restEvents
      : (() => {
          const seen = new Set(restEvents.map((e) => e.id))
          return [...liveEvents.filter((e) => e.id && !seen.has(e.id)), ...restEvents]
        })()
    // Deduplicate by normalized title prefix (catches same-article duplicate IDs from FalconFeeds)
    const titleSeen = new Set<string>()
    const deduped: typeof raw = []
    for (const e of raw) {
      const key = e.title.slice(0, 60).toLowerCase().replace(/\s+/g, ' ').trim()
      if (!titleSeen.has(key)) { titleSeen.add(key); deduped.push(e) }
      if (deduped.length >= 500) break
    }
    return deduped
  }, [eventsData, liveEvents])

  const spreadCoords = useMemo(() => getSpreadEventCoordinates(events), [events])

  const arcs: ArcData[] = useMemo(() => {
    // Only show arcs when at least one relevant layer is active
    if (!layers.cyber && !layers.conflict && !layers.natural && !layers.weather) return []

    const filtered = events
      .filter((e) => e.domain === 'cyber' || e.domain === 'geopolitical')
      .slice(0, 120)
    return filtered
      .filter((e) => spreadCoords.has(e.id))
      .map((e) => {
        const pos = spreadCoords.get(e.id)!
        const targetLat = pos.lat
        const targetLng = pos.lng

        const [attackerLat, attackerLng] = getRandomAttackerOrigin()

        // Derive arc visual style: prefer FalconFeeds category when available
        let arcTypeKey = e.event_type || 'default'
        const ffCategory = (e.metadata?.category || '').toLowerCase()
        if ((e.source === 'falconfeeds' || e.source === 'falconfeeds_cyber') && ffCategory) {
          if (ffCategory.includes('ddos')) arcTypeKey = 'ff_ddos'
          else if (ffCategory.includes('ransomware')) arcTypeKey = 'ff_ransomware'
          else if (ffCategory.includes('breach') || ffCategory.includes('leak')) arcTypeKey = 'ff_breach'
        }
        const arcStyle = ARC_TYPE_COLORS[arcTypeKey] || ARC_TYPE_COLORS.default

        let dashLen = 0.35
        let dashGap = 0.2
        let animateTime = 8000
        let stroke = 0.3

        switch (arcTypeKey) {
          case 'ransomware':
            dashLen = 0.2; dashGap = 0.12; animateTime = 5000; stroke = 0.4
            break
          case 'vulnerability':
            dashLen = 0.5; dashGap = 0.25; animateTime = 10000; stroke = 0.25
            break
          case 'cyber_attack':
            dashLen = 0.3; dashGap = 0.15; animateTime = 6000; stroke = 0.35
            break
          case 'ff_ddos':
            dashLen = 0.18; dashGap = 0.1; animateTime = 4500; stroke = 0.38
            break
          case 'ff_breach':
            dashLen = 0.6; dashGap = 0.25; animateTime = 9000; stroke = 0.32
            break
          case 'ff_ransomware':
            dashLen = 0.25; dashGap = 0.12; animateTime = 5500; stroke = 0.45
            break
          case 'conflict':
          case 'explosion':
          case 'protest':
            dashLen = 0.7; dashGap = 0.1; animateTime = 7000; stroke = 0.5
            break
          case 'disaster':
            dashLen = 0.6; dashGap = 0.2; animateTime = 9000; stroke = 0.3
            break
        }

        if (e.severity === 'critical') stroke *= 1.3

        return {
          startLat: attackerLat,
          startLng: attackerLng,
          endLat: targetLat,
          endLng: targetLng,
          color: arcStyle.main,
          color2: arcStyle.trail,
          label: e.title,
          severity: e.severity,
          stroke,
          eventType: arcTypeKey,
          dashLen,
          dashGap,
          animateTime,
          event: e,
        } as ArcData
      })
  }, [events, layers.cyber, layers.conflict, layers.natural, layers.weather, spreadCoords])

  const rings: RingData[] = useMemo(() => {
    return events
      .filter((e) => {
        if (e.severity !== 'critical' && e.severity !== 'high') return false
        return spreadCoords.has(e.id)
      })
      .slice(0, 30)
      .map((e) => {
        const pos = spreadCoords.get(e.id)!
        const isCritical = e.severity === 'critical'
        return {
          lat: pos.lat,
          lng: pos.lng,
          maxR: isCritical ? 5 : 2.5,
          propagationSpeed: isCritical ? 3 : 1.5,
          repeatPeriod: isCritical ? 1500 : 2500,
          color: isCritical ? 'rgba(255, 34, 85, 0.5)' : 'rgba(255, 107, 43, 0.3)',
        } as RingData
      })
  }, [events, spreadCoords])

  const flightArcs: ArcData[] = useMemo(() => {
    if (!layers.flights || !liveFlightsData?.aircraft?.length) return []
    const rad = (deg: number) => (deg * Math.PI) / 180
    return liveFlightsData.aircraft
      .filter((a) => a.latitude != null && a.longitude != null && (a.true_track != null || a.velocity != null))
      .slice(0, 150)
      .map((a) => {
        const lat = a.latitude!
        const lng = a.longitude!
        const heading = a.true_track ?? 0
        const velMs = a.velocity ?? 100
        const distDeg = Math.min(0.4, 0.05 + (velMs / 250) * 0.2)
        const dLat = distDeg * Math.cos(rad(heading))
        const dLng = distDeg * Math.sin(rad(heading)) / Math.max(0.01, Math.cos(rad(lat)))
        return {
          startLat: lat,
          startLng: lng,
          endLat: lat + dLat,
          endLng: lng + dLng,
          color: '#22c55e',
          color2: '#22c55e40',
          label: a.callsign || a.icao24,
          severity: 'info',
          stroke: 0.25,
          eventType: 'flight',
          dashLen: 0.3,
          dashGap: 0.05,
          animateTime: 2000,
        } as ArcData
      })
  }, [layers.flights, liveFlightsData?.aircraft])

  const gpsSpoofingArcs: ArcData[] = useMemo(() => {
    if (!layers.gpsjam || !gpsJamSpoofingData?.features?.length) return []
    return gpsJamSpoofingData.features
      .filter((f) => f.geometry?.coordinates?.length >= 2)
      .slice(0, 100)
      .map((f) => {
        const coords = f.geometry.coordinates
        const [startLng, startLat] = coords[0]
        const [endLng, endLat] = coords[coords.length - 1]
        const props = f.properties || {}
        const flightId = (props.flight_id as string) || 'GPS spoofing'
        return {
          startLat,
          startLng,
          endLat,
          endLng,
          color: '#dc2626',
          color2: '#dc262640',
          label: flightId,
          severity: 'high',
          stroke: 0.35,
          eventType: 'gps_spoofing',
          dashLen: 0.2,
          dashGap: 0.1,
          animateTime: 2500,
        } as ArcData
      })
  }, [layers.gpsjam, gpsJamSpoofingData])

  const topCountry = useMemo(() => {
    const scores = escalation?.country_scores
    if (!scores || scores.length === 0) return null
    const top = scores[0]
    return { code: top.country_code, name: top.country_name, score: top.instability_score }
  }, [escalation])

  const handleAutoZoom = useCallback((lat: number, lng: number) => {
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat, lng, altitude: 1.5 }, 2000)
    }
  }, [])

  const handleFilterHighSev = useCallback(() => {
    setSeverityFilter('critical,high')
  }, [])

  const handleCountryClick = useCallback((countryCode: string) => {
    setSelectedCountry(countryCode)
  }, [])

  const handleParamsChange = useCallback((next: MonitorEventsParams) => {
    setParams(next)
    setSelectedEvent(null)
  }, [])

  const handleEventClick = useCallback((event: MonitorEventItem) => {
    setSelectedEvent(event)
    setSelectedAircraft(null)
    setSelectedVessel(null)
    setSelectedGamma(null)
    setSelectedBase(null)
  }, [])

  const handleAircraftClick = useCallback((aircraft: MonitorLiveAircraft) => {
    setSelectedAircraft(aircraft)
    setSelectedEvent(null)
    setSelectedVessel(null)
    setSelectedGamma(null)
    setSelectedBase(null)
    if (globeRef.current && aircraft.latitude != null && aircraft.longitude != null) {
      globeRef.current.pointOfView({ lat: aircraft.latitude, lng: aircraft.longitude, altitude: 0.5 }, 800)
    }
  }, [])

  const handleVesselClick = useCallback((vessel: MonitorLiveVessel) => {
    setSelectedVessel(vessel)
    setSelectedEvent(null)
    setSelectedAircraft(null)
    setSelectedGamma(null)
    setSelectedBase(null)
    if (globeRef.current && vessel.latitude != null && vessel.longitude != null) {
      globeRef.current.pointOfView({ lat: vessel.latitude, lng: vessel.longitude, altitude: 0.5 }, 800)
    }
  }, [])

  const handleGammaClick = useCallback((facility: GammaIrradiatorFacility) => {
    setSelectedGamma(facility)
    setSelectedEvent(null)
    setSelectedAircraft(null)
    setSelectedVessel(null)
    setSelectedBase(null)
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat: facility.lat, lng: facility.lon, altitude: 0.5 }, 800)
    }
  }, [])

  const handleBaseClick = useCallback((base: MilitaryBase) => {
    setSelectedBase(base)
    setSelectedEvent(null)
    setSelectedAircraft(null)
    setSelectedVessel(null)
    setSelectedGamma(null)
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat: base.lat, lng: base.lon, altitude: 0.5 }, 800)
    }
  }, [])

  // Phase 2: Ticker/list click → zoom globe to event
  useEffect(() => {
    if (!selectedEvent || !globeRef.current) return
    let lat = selectedEvent.latitude ?? null
    let lng = selectedEvent.longitude ?? null
    if (lat == null || lng == null) {
      const coords = selectedEvent.country_code ? getCountryLatLng(selectedEvent.country_code) : null
      if (!coords) return
      lat = coords[0]
      lng = coords[1]
    }
    globeRef.current.pointOfView({ lat, lng, altitude: 0.45 }, 1200)
  }, [selectedEvent])

  // Flash rings: burst animation on the globe when live events arrive
  useEffect(() => {
    if (liveEvents.length === 0) return
    if (!liveInitialized.current) {
      // Mark initial batch as seen without flashing
      liveEvents.forEach(e => { if (e.id) seenLiveIds.current.add(e.id) })
      liveInitialized.current = true
      return
    }
    const newEvents = liveEvents.filter(e => e.id && !seenLiveIds.current.has(e.id))
    if (newEvents.length === 0) return
    newEvents.forEach(e => { if (e.id) seenLiveIds.current.add(e.id) })
    const now = Date.now()
    const newRings: FlashRing[] = newEvents
      .filter(e => e.latitude != null && e.longitude != null)
      .map(e => ({
        lat: e.latitude!,
        lng: e.longitude!,
        maxR: e.severity === 'critical' ? 12 : e.severity === 'high' ? 8 : e.severity === 'medium' ? 5 : 3,
        propagationSpeed: e.severity === 'critical' ? 9 : e.severity === 'high' ? 6 : 4,
        repeatPeriod: e.severity === 'critical' ? 300 : 500,
        color: flashColorForEvent(e.domain, e.severity),
        _expiresAt: now + (e.severity === 'critical' ? 5000 : 3500),
      }))
    if (newRings.length > 0) {
      setFlashRings(prev => [...prev, ...newRings].slice(-30))
    }

    // Toasts — one per new event, capped at 4 visible
    const toastDuration = 4500
    const newToasts: LiveToast[] = newEvents.map(e => ({
      id: `${e.id}-${now}`,
      domain: e.domain,
      severity: e.severity,
      title: e.title.slice(0, 90),
      country: e.country_name || e.country_code || null,
      color: flashColorForEvent(e.domain, e.severity),
      expiresAt: now + toastDuration,
    }))
    if (newToasts.length > 0) {
      setLiveToasts(prev => [...newToasts, ...prev].slice(0, 4))
    }

    // DDoS-specific alerts — prominent panel, 8s duration
    const ddosEvents = newEvents.filter(isDdosEvent)
    if (ddosEvents.length > 0) {
      const newAlerts: DdosAlert[] = ddosEvents.map(e => ({
        id: `ddos-${e.id}-${now}`,
        title: e.title.slice(0, 120),
        country: e.country_name || e.country_code || null,
        severity: e.severity,
        ts: now,
        expiresAt: now + 8000,
      }))
      setDdosAlerts(prev => [...newAlerts, ...prev].slice(0, 3))
    }
  }, [liveEvents])

  // Expire flash rings after their duration
  useEffect(() => {
    if (flashRings.length === 0) return
    const earliest = Math.min(...flashRings.map(r => r._expiresAt))
    const delay = Math.max(earliest - Date.now(), 0) + 50
    const timer = setTimeout(() => {
      const now = Date.now()
      setFlashRings(prev => prev.filter(r => r._expiresAt > now))
    }, delay)
    return () => clearTimeout(timer)
  }, [flashRings])

  // Expire toasts
  useEffect(() => {
    if (liveToasts.length === 0) return
    const earliest = Math.min(...liveToasts.map(t => t.expiresAt))
    const delay = Math.max(earliest - Date.now(), 0) + 50
    const timer = setTimeout(() => {
      const now = Date.now()
      setLiveToasts(prev => prev.filter(t => t.expiresAt > now))
    }, delay)
    return () => clearTimeout(timer)
  }, [liveToasts])

  // Expire DDoS alerts
  useEffect(() => {
    if (ddosAlerts.length === 0) return
    const earliest = Math.min(...ddosAlerts.map(a => a.expiresAt))
    const delay = Math.max(earliest - Date.now(), 0) + 50
    const timer = setTimeout(() => {
      const now = Date.now()
      setDdosAlerts(prev => prev.filter(a => a.expiresAt > now))
    }, delay)
    return () => clearTimeout(timer)
  }, [ddosAlerts])

  // Seed toasts + DDoS alerts from REST events on first load (not WS-dependent)
  useEffect(() => {
    if (eventsSeeded.current || events.length === 0) return
    eventsSeeded.current = true
    const now = Date.now()
    const tenMinAgo = now - 10 * 60 * 1000
    const sixtyMinAgo = now - 60 * 60 * 1000

    // Show toasts for events ingested in the last 10 min
    // Append 'Z' — API timestamps lack timezone suffix so browsers parse as local time
    const utc = (s: string) => new Date(s.endsWith('Z') ? s : s + 'Z').getTime()
    const recentEvents = events
      .filter(e => utc(e.ingested_at || e.timestamp) > tenMinAgo)
      .slice(0, 4)
    if (recentEvents.length > 0) {
      setLiveToasts(recentEvents.map(e => ({
        id: `seed-${e.id}`,
        domain: e.domain,
        severity: e.severity,
        title: e.title.slice(0, 90),
        country: e.country_name || e.country_code || null,
        color: flashColorForEvent(e.domain, e.severity),
        expiresAt: now + 5000,
      })))
    }

    // Show DDoS alerts for DDoS events ingested in the last 60 min
    const recentDdos = events
      .filter(e => {
        if (shownDdosIds.current.has(e.id)) return false
        if (!isDdosEvent(e)) return false
        return utc(e.ingested_at || e.timestamp) > sixtyMinAgo
      })
      .slice(0, 3)
    if (recentDdos.length > 0) {
      recentDdos.forEach(e => shownDdosIds.current.add(e.id))
      setDdosAlerts(recentDdos.map(e => ({
        id: `ddos-seed-${e.id}`,
        title: e.title.slice(0, 120),
        country: e.country_name || e.country_code || null,
        severity: e.severity,
        ts: now,
        expiresAt: now + 10000,
      })))
    }
  }, [events])

  const isDataLoading = eventsLoading || eventsFetching

  const handleLeftPanelDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }, [])

  const handleLeftPanelDrop = useCallback(
    (targetId: LeftPanelSectionId, e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      const sourceId = e.dataTransfer.getData('text/plain') as LeftPanelSectionId | null
      if (!sourceId || sourceId === targetId || !leftPanelOrder.includes(sourceId)) return
      const next = [...leftPanelOrder]
      const fromIdx = next.indexOf(sourceId)
      const toIdx = next.indexOf(targetId)
      if (fromIdx === -1 || toIdx === -1) return
      next.splice(fromIdx, 1)
      next.splice(toIdx, 0, sourceId)
      setLeftPanelOrder(next)
    },
    [leftPanelOrder]
  )

  const handleLeftPanelDragStart = useCallback((id: LeftPanelSectionId, e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/plain', id)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const renderLeftPanelSection = (id: LeftPanelSectionId) => {
    switch (id) {
      case 'layers':
        return <LayerToggles layers={layers} onChange={setLayers} />
      case 'escalation':
        return (
          <EscalationMeter
            data={escalation || null}
            loading={escalationLoading}
            onCountryClick={handleCountryClick}
            onScoreClick={() => setShowEscalationDetail(true)}
          />
        )
      case 'signals':
        return (
          correlationData &&
          correlationData.signals.length > 0 && (
            <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded p-2.5 shadow-[0_0_15px_rgba(34,197,94,0.06)]">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shadow-[0_0_6px_rgba(245,158,11,0.5)]" />
                <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
                  Cross-Domain Signals
                </span>
                <span className="text-[9px] text-amber-500 font-mono ml-auto">
                  {correlationData.signals.length}
                </span>
              </div>
              <div className="space-y-1">
                {correlationData.signals.slice(0, 4).map((sig, i) => (
                  <button
                    key={i}
                    onClick={() => handleCountryClick(sig.country_code)}
                    className="w-full flex items-center justify-between text-[9px] font-mono px-1.5 py-1 rounded hover:bg-green-500/10 transition-colors"
                  >
                    <span className="text-gray-400">{sig.country_name || sig.country_code}</span>
                    <div className="flex items-center gap-1.5">
                      {sig.domains_active.map((d) => (
                        <span
                          key={d}
                          className={`w-1 h-1 rounded-full ${
                            d === 'cyber'
                              ? 'bg-purple-500'
                              : d === 'conflict'
                                ? 'bg-red-500'
                                : d === 'infrastructure'
                                  ? 'bg-amber-500'
                                  : 'bg-blue-500'
                          }`}
                        />
                      ))}
                      <span
                        className={`text-[8px] uppercase ${
                          sig.signal_level === 'critical'
                            ? 'text-red-400'
                            : sig.signal_level === 'high'
                              ? 'text-orange-400'
                              : 'text-yellow-400'
                        }`}
                      >
                        {sig.signal_level}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )
        )
      case 'filters':
        return <EventFilters config={config || null} params={params} onChange={handleParamsChange} activeLayers={layers} />
      case 'news':
        return (
          <MonitorNewsPanel
            items={newsData?.items ?? []}
            loading={newsLoading}
            onRefresh={() => refetchNews()}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-950 flex flex-col overflow-hidden">
      <SEOHead
        title="Live OSINT Threat Monitor - Real-Time Cyber & Geopolitical Intelligence | Sycek"
        description="Real-time global threat monitoring dashboard. Track live cyber attacks, DDoS incidents, ransomware, geopolitical conflicts, sanctions, maritime activity, and flight surveillance on an interactive 3D globe."
        keywords="live threat monitor, real-time cyber intelligence, DDoS tracker, ransomware monitor, geopolitical conflict map, OSINT dashboard, threat intelligence feed, cyber attack map, global security monitor, maritime surveillance, flight tracker, sanctions monitoring"
        canonical="/monitor"
        jsonLd={{
          "@type": "WebApplication",
          "name": "Sycek Live OSINT Monitor",
          "url": "https://sycek.io/monitor",
          "applicationCategory": "SecurityApplication",
          "description": "Real-time global threat monitoring with live cyber attack tracking, geopolitical intelligence, maritime and flight surveillance on an interactive 3D globe.",
          "operatingSystem": "Web",
          "provider": { "@type": "Organization", "name": "Sycek OSINT", "url": "https://sycek.io" }
        }}
      />
      {/* Command Bar */}
      <StatsBar
        stats={stats || null}
        loading={statsLoading}
        onFilterBySeverity={setSeverityFilter}
        activeSeverityFilter={severityFilter}
      />

      <div className="flex-1 relative overflow-hidden">
        {/* Globe — centered in available space, responsive */}
        <div className="absolute inset-0 flex items-stretch justify-center">
          <div className="w-full max-w-[1480px] relative">
          {/* Flight / vessel detail popup on the globe (not in right sidebar) */}
          {(selectedAircraft || selectedVessel || selectedGamma || selectedBase) && !selectedEvent && (
            <div className="absolute left-1/2 bottom-20 z-20 w-80 -translate-x-1/2 pointer-events-auto shadow-2xl">
              {selectedAircraft && (
                <FlightDetailCard aircraft={selectedAircraft} onClose={() => setSelectedAircraft(null)} />
              )}
              {selectedVessel && !selectedAircraft && (
                <VesselDetailCard vessel={selectedVessel} onClose={() => setSelectedVessel(null)} />
              )}
              {selectedGamma && !selectedAircraft && !selectedVessel && (
                <GammaDetailCard facility={selectedGamma} onClose={() => setSelectedGamma(null)} />
              )}
              {selectedBase && !selectedAircraft && !selectedVessel && !selectedGamma && (
                <MilitaryBaseDetailCard base={selectedBase} onClose={() => setSelectedBase(null)} />
              )}
            </div>
          )}
            <GlobeView
              events={events}
              arcs={arcs}
              rings={flashRings.length > 0 ? [...rings, ...flashRings] : rings}
              selectedEvent={selectedEvent}
              onEventClick={handleEventClick}
              maxZoom={config?.max_zoom || 5}
              colorBy={colorBy}
              globeRefCallback={(ref) => { globeRef.current = ref }}
              vessels={layers.maritime ? (liveVesselsData?.vessels ?? []).filter(
                (v) => v.latitude != null && v.longitude != null
                  && Math.abs(v.latitude) <= 90 && Math.abs(v.longitude) <= 180
              ) : []}
              flights={layers.flights ? (liveFlightsData?.aircraft ?? []) : []}
              flightArcs={flightArcs}
              selectedAircraft={selectedAircraft}
              onAircraftClick={handleAircraftClick}
              onVesselClick={handleVesselClick}
              onCountryClick={handleCountryClick}
              onGammaClick={handleGammaClick}
              onBaseClick={handleBaseClick}
              gammaFacilities={layers.gamma ? (gammaData?.facilities ?? []) : []}
              militaryBases={layers.bases ? (basesData?.bases ?? []) : []}
              gpsJamCells={layers.gpsjam ? (gpsJamJammingData?.data ?? []) : []}
              gpsSpoofingArcs={gpsSpoofingArcs}
            />
          </div>
        </div>

        {/* AIS not configured notice */}
        {layers.maritime && liveVesselsData && (liveVesselsData as any).data_source == null && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
            <div className="bg-gray-950/90 border border-blue-900/40 rounded px-3 py-1.5 text-[10px] font-mono text-blue-400/70">
              Maritime layer: set AISSTREAM_API_KEY for real vessel positions
            </div>
          </div>
        )}

        {/* Loading overlay */}
        <LoadingOverlay visible={isDataLoading} />

        {/* Crisis mode overlay */}
        <CrisisMode
          compositeScore={escalation?.composite_score ?? 0}
          level={escalation?.level ?? 'normal'}
          topCountry={topCountry}
          onAutoZoom={handleAutoZoom}
          onFilterHighSev={handleFilterHighSev}
        />

        {/* Scan line effect */}
        <div
          className="absolute inset-0 pointer-events-none z-20 opacity-[0.03]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(6,182,212,0.1) 2px, rgba(6,182,212,0.1) 4px)',
          }}
        />

        {/* Left intel panel (draggable cards) — spacing and flex-shrink to prevent overlap */}
        <div className="absolute top-3 left-3 z-10 w-72 max-h-[calc(100vh-6rem)] overflow-y-auto scrollbar-thin pr-2 flex flex-col gap-3">
          {leftPanelOrder.map((id) => (
            <div
              key={id}
              draggable
              onDragStart={(e) => handleLeftPanelDragStart(id, e)}
              onDragOver={handleLeftPanelDragOver}
              onDrop={(e) => handleLeftPanelDrop(id, e)}
              className="cursor-move flex-shrink-0"
            >
              {renderLeftPanelSection(id)}
            </div>
          ))}

          {/* Controls row */}
          <div className="flex items-center gap-1 flex-wrap flex-shrink-0">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-1 px-2 py-1 text-[9px] font-mono bg-gray-900/80 backdrop-blur border border-green-900/50 rounded text-gray-400 hover:text-green-400 hover:border-green-500/50 hover:shadow-[0_0_8px_rgba(74,222,128,0.15)] transition-all"
              title="Search (Ctrl+K)"
            >
              <Search className="w-3 h-3" /> SEARCH
            </button>
            <button
              onClick={() => refetchEvents()}
              className="flex items-center gap-1 px-2 py-1 text-[9px] font-mono bg-gray-900/80 backdrop-blur border border-green-900/50 rounded text-gray-400 hover:text-green-400 hover:border-green-500/50 hover:shadow-[0_0_8px_rgba(74,222,128,0.15)] transition-all"
            >
              <RefreshCw className={`w-3 h-3 ${eventsFetching ? 'animate-spin' : ''}`} /> REFRESH
            </button>
            <button
              onClick={() => setShowList((v) => !v)}
              className="flex items-center gap-1 px-2 py-1 text-[9px] font-mono bg-gray-900/80 backdrop-blur border border-green-900/50 rounded text-gray-400 hover:text-green-400 hover:border-green-500/50 hover:shadow-[0_0_8px_rgba(74,222,128,0.15)] transition-all"
            >
              <List className="w-3 h-3" /> {showList ? 'HIDE' : 'LIST'}
            </button>
            <button
              onClick={() => setColorBy((v) => (v === 'severity' ? 'domain' : 'severity'))}
              className="flex items-center gap-1 px-2 py-1 text-[9px] font-mono bg-gray-900/80 backdrop-blur border border-green-900/50 rounded text-gray-400 hover:text-green-400 hover:border-green-500/50 hover:shadow-[0_0_8px_rgba(74,222,128,0.15)] transition-all"
            >
              {colorBy === 'severity' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {colorBy === 'severity' ? 'SEV' : 'DOM'}
            </button>
            <button
              onClick={() => setWsEnabled((v) => !v)}
              className={`flex items-center gap-1 px-2 py-1 text-[9px] font-mono bg-gray-900/80 backdrop-blur border rounded transition-all ${
                wsConnected
                  ? 'border-emerald-500/40 text-emerald-400'
                  : 'border-gray-700/50 text-gray-600'
              }`}
              title={wsConnected ? 'Live connected' : 'Live disconnected'}
            >
              {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              LIVE
            </button>
          </div>

          {/* Intel summary */}
          <div className="text-[10px] text-gray-600 font-mono px-1 flex-shrink-0">
            {eventsData
              ? `${eventsData.total.toLocaleString()} events · ${events.length} rendered · ${arcs.length} vectors`
              : eventsLoading
                ? 'Acquiring targets...'
                : 'No intelligence data'}
          </div>

          {/* Event list */}
          {showList && (
            <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded max-h-[35vh] overflow-y-auto flex-shrink-0 shadow-[0_0_15px_rgba(34,197,94,0.06)]">
              {events.length === 0 ? (
                <div className="px-4 py-6 text-center text-[10px] font-mono text-gray-600">
                  {eventsLoading || eventsFetching
                    ? 'Loading…'
                    : 'No events for this filter. Turn on Cyber for FalconFeeds data (ingestion runs ~every 15 min). Try broadening time range or check back later.'}
                </div>
              ) : (
                <ul className="divide-y divide-gray-800/30">
                  {events.slice(0, 50).map((ev) => (
                    <li key={ev.id}>
                      <button
                        onClick={() => handleEventClick(ev)}
                        className={`w-full text-left px-3 py-1.5 hover:bg-green-500/10 transition-colors ${
                          selectedEvent?.id === ev.id ? 'bg-green-500/15 border-l-2 border-green-400 shadow-[0_0_8px_rgba(74,222,128,0.12)]' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 shadow-[0_0_6px] ${
                            ev.severity === 'critical' ? 'bg-red-500 shadow-red-500/50' :
                            ev.severity === 'high' ? 'bg-orange-500 shadow-orange-500/50' :
                            ev.severity === 'medium' ? 'bg-yellow-500 shadow-yellow-500/50' :
                            'bg-blue-500 shadow-blue-500/50'
                          }`} />
                          <div className="min-w-0">
                            <p className="text-[11px] text-gray-300 leading-snug truncate">{ev.title}</p>
                            <p className="text-[9px] text-gray-600 font-mono mt-0.5">
                              {ev.source} · {getTimeAgo(new Date(ev.timestamp))}
                              {ev.country_name ? ` · ${ev.country_name}` : ''}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* DDoS alert panel — top-center, pulsing on new attacks */}
        {ddosAlerts.length > 0 && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex flex-col gap-2 items-center pointer-events-none" style={{ width: 420 }}>
            <style>{`
              @keyframes ddosPulse{0%,100%{box-shadow:0 0 12px rgba(239,68,68,.5),0 0 24px rgba(239,68,68,.2)}50%{box-shadow:0 0 22px rgba(239,68,68,.8),0 0 44px rgba(239,68,68,.4)}}
              @keyframes ddosIn{0%{opacity:0;transform:translateY(-10px) scale(.96)}10%{opacity:1;transform:translateY(0) scale(1)}80%{opacity:1}100%{opacity:0}}
            `}</style>
            {ddosAlerts.map((a) => (
              <div
                key={a.id}
                className="w-full rounded-xl border border-red-500/60 bg-gray-950/97 backdrop-blur-md px-4 py-3"
                style={{ animation: `ddosPulse 1.8s ease-in-out infinite, ddosIn ${(a.expiresAt - Date.now()) / 1000 + 0.5}s ease forwards` }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-red-400" />
                    <Zap className="w-3 h-3 text-red-500 animate-pulse" />
                    <span className="text-[11px] font-mono font-bold text-red-400 uppercase tracking-widest">DDoS Attack Detected</span>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5">
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">{a.severity}</span>
                    {a.country && <span className="text-[9px] font-mono text-gray-500">{a.country}</span>}
                  </div>
                </div>
                <p className="text-[12px] text-gray-100 font-mono leading-snug">{a.title}</p>
              </div>
            ))}
          </div>
        )}

        {/* Right detail panel — scrollable so Markets/Stocks cards stay visible */}
        <div className="absolute top-3 right-3 z-10 w-80 max-h-[calc(100vh-5rem)] overflow-y-auto space-y-2 pointer-events-auto">
          {selectedEvent && (
            <>
              <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)} />
              {selectedEvent.latitude != null && selectedEvent.longitude != null && (
                <EventMap3D
                  lat={selectedEvent.latitude}
                  lng={selectedEvent.longitude}
                  label={selectedEvent.country_name || selectedEvent.country_code || ''}
                />
              )}
            </>
          )}
          {/* Prediction markets — only show when markets exist */}
          {(() => {
            const currentMarkets = polymarketData?.markets ?? []
            if (!polymarketLoading && currentMarkets.length === 0) return null
            return (
          <div className="bg-gray-950/90 backdrop-blur border border-purple-900/40 rounded px-3 py-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-green-400">
                Prediction Markets
              </span>
              {currentMarkets.length > 0 && (
                <span className="text-[9px] text-gray-500 font-mono">
                  {currentMarkets.length}
                </span>
              )}
            </div>
            {polymarketLoading && !polymarketData ? (
              <div className="text-[10px] font-mono text-gray-500 py-2">Loading…</div>
            ) : currentMarkets.length > 0 ? (
              <div className="space-y-1">
                {currentMarkets.slice(0, 4).map((m) => (
                  <div
                    key={m.id}
                    className="text-[10px] text-gray-300 border border-purple-900/40 rounded px-2 py-1 bg-purple-950/30"
                  >
                    <div className="truncate">{m.question}</div>
                    <div className="flex items-center justify-between mt-0.5 text-[9px] text-gray-500 font-mono">
                      <span className="truncate max-w-[70%]">
                        {(m.outcome || 'Yes').slice(0, 18)}
                      </span>
                      {typeof m.probability === 'number' && (
                        <span className="text-purple-300">
                          {(m.probability * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
            )
          })()}

          {/* Stock indices — always show card */}
          <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded px-3 py-2 shadow-[0_0_15px_rgba(34,197,94,0.06)]">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-green-400">
                Commodities
              </span>
            </div>
            {stockSummaryLoading && !stockSummary ? (
              <div className="text-[10px] font-mono text-gray-500 py-2">Loading…</div>
            ) : stockSummary && stockSummary.indices.length > 0 ? (
              <div className="space-y-0.5">
                {stockSummary.indices.slice(0, 4).map((idx) => {
                  const change = idx.change ?? 0
                  const pct = idx.changePercent ?? 0
                  const positive = change >= 0
                  return (
                    <div
                      key={idx.symbol}
                      className="flex items-center justify-between text-[10px] font-mono text-gray-300"
                    >
                      <span className="truncate max-w-[45%]">{idx.name}</span>
                      <span className="text-gray-400">
                        {idx.price != null ? idx.price.toFixed(2) : '--'}
                      </span>
                      <span
                        className={`text-[9px] ${
                          positive ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {change.toFixed(2)} ({pct.toFixed(2)}%)
                      </span>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-[10px] font-mono text-gray-500 py-2">No data</div>
            )}
          </div>

          {/* Cloudflare Radar traffic anomalies — only show when data is available (hide on token error) */}
          {cloudflareOutages && cloudflareOutages.traffic?.success !== false && (
            <div className="bg-gray-950/90 backdrop-blur border border-rose-900/40 rounded px-3 py-2">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-green-400">
                  Traffic Anomalies (Radar)
                </span>
              </div>
              <div className="text-[10px] text-gray-400 font-mono">
                {cloudflareOutages.traffic?.success === true ? (
                  (cloudflareOutages.traffic?.trafficAnomalies?.length ?? 0) > 0 ? (
                    <>
                      <p className="mb-1.5 text-rose-300/90">
                        {cloudflareOutages.traffic?.trafficAnomalies?.length ?? 0} anomaly(ies) detected.
                      </p>
                      <ul className="space-y-1 max-h-24 overflow-y-auto text-[9px]">
                        {((cloudflareOutages.traffic?.trafficAnomalies ?? []) as Array<{
                          uuid?: string
                          type?: string
                          status?: string
                          startDate?: string
                          locationDetails?: { code?: string; name?: string }
                          asnDetails?: { name?: string; locations?: { code?: string; name?: string }[] }
                        }>).slice(0, 10).map((a, i) => (
                          <li key={a.uuid ?? i} className="border-t border-rose-900/20 pt-1 first:border-0 first:pt-0">
                            {a.locationDetails?.name || a.locationDetails?.code || a.asnDetails?.name || 'Unknown'} · {a.type ?? '—'} · {a.status ?? '—'}
                            {a.startDate && <span className="text-gray-600 ml-1">{new Date(a.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <>
                      <p className="mb-0.5">Cloudflare Radar — traffic anomalies.</p>
                      <p className="text-[9px] text-gray-500">No anomalies detected right now.</p>
                    </>
                  )
                ) : cloudflareOutages.traffic?.success === false ? (
                  <>
                    <p className="mb-0.5">Cloudflare Radar API.</p>
                    <p className="text-[9px] text-amber-500">API error — check token scope (Radar read).</p>
                  </>
                ) : (
                  <>
                    <p className="mb-0.5">Cloudflare Radar API — traffic anomalies.</p>
                    <p className="text-[9px] text-gray-600">Set CLOUDFLARE_API_TOKEN in env (Docker: infra/.env).</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Telegram live feed — messages from monitored groups/channels */}
          <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded-lg px-4 py-3 min-h-[280px] shadow-[0_0_15px_rgba(34,197,94,0.06)]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-green-400 flex items-center gap-1.5">
                <MessageCircle className="w-4 h-4" />
                Telegram Live
              </span>
              {telegramFeed && telegramFeed.messages.length > 0 && (
                <span className="text-[10px] text-gray-500 font-mono">
                  {telegramFeed.messages.length}
                </span>
              )}
            </div>
            {telegramFeedLoading && !telegramFeed ? (
              <div className="text-[11px] font-mono text-gray-500 py-3">Loading…</div>
            ) : telegramFeedError ? (
              <div className="text-[11px] font-mono text-gray-500 py-3">
                Could not load Telegram feed
              </div>
            ) : telegramFeed && telegramFeed.messages.length > 0 ? (
              <ul className="space-y-2 max-h-[240px] overflow-y-auto text-[11px]">
                {telegramFeed.messages.slice(0, 25).map((msg: any, i: number) => {
                  const text = (msg.text ?? msg.message ?? msg.content ?? '').toString().trim()
                  const group = (msg.group_username ?? '').toString()
                  const dateStr = msg.date
                    ? new Date(msg.date).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : ''
                  const mediaUrl: string = msg.media || msg.media_url || msg.photo || ''
                  return (
                    <li
                      key={msg.id ?? i}
                      className="border-t border-sky-900/20 pt-2 first:border-0 first:pt-0"
                    >
                      <div className="flex items-center gap-2 text-[10px] text-green-400/80 font-mono mb-1">
                        {group && <span>@{group}</span>}
                        {dateStr && <span className="text-gray-600">{dateStr}</span>}
                      </div>
                      <div className="flex gap-2">
                        <div className="text-gray-300 line-clamp-2 break-words text-[11px] flex-1">
                          {text || '—'}
                        </div>
                        {mediaUrl && (
                          <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                            <img
                              src={mediaUrl}
                              alt=""
                              className="w-14 h-14 rounded object-cover border border-green-900/30 hover:border-green-400/50 transition-colors"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                            />
                          </a>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="text-[11px] font-mono text-gray-500 py-3">
                No messages yet. Add groups in Admin → Telegram.
              </div>
            )}
          </div>

          {/* Twitter/X live feed — tweets matching admin-configured keywords */}
          <div className="bg-gray-950/95 backdrop-blur border border-sky-900/50 rounded-lg px-4 py-3 min-h-[280px] shadow-[0_0_15px_rgba(56,189,248,0.06)]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-sky-400 flex items-center gap-1.5">
                <Hash className="w-4 h-4" />
                X / Twitter Live
              </span>
              {twitterFeed && twitterFeed.tweets.length > 0 && (
                <span className="text-[10px] text-gray-500 font-mono">
                  {twitterFeed.tweets.length}
                </span>
              )}
            </div>
            {twitterFeedLoading && !twitterFeed ? (
              <div className="text-[11px] font-mono text-gray-500 py-3">Loading…</div>
            ) : twitterFeedError ? (
              <div className="text-[11px] font-mono text-gray-500 py-3">
                Could not load Twitter feed
              </div>
            ) : twitterFeed && twitterFeed.tweets.length > 0 ? (
              <ul className="space-y-2 max-h-[240px] overflow-y-auto text-[11px]">
                {twitterFeed.tweets.slice(0, 20).map((tweet: any, i: number) => {
                  const text = (tweet.text ?? '').toString().trim()
                  const author = tweet.author?.username ?? tweet.author?.name ?? ''
                  const dateStr = tweet.created_at
                    ? new Date(tweet.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : ''
                  const likes = tweet.metrics?.likes ?? 0
                  const rts = tweet.metrics?.retweets ?? 0
                  const mediaList: any[] = tweet.media ?? []
                  const firstMedia = mediaList[0]
                  return (
                    <li
                      key={tweet.id ?? i}
                      className="border-t border-sky-900/20 pt-2 first:border-0 first:pt-0"
                    >
                      <div className="flex items-center gap-2 text-[10px] text-sky-400/80 font-mono mb-1">
                        {author && <span>@{author}</span>}
                        {dateStr && <span className="text-gray-600">{dateStr}</span>}
                        {(likes > 0 || rts > 0) && (
                          <span className="text-gray-600 ml-auto">
                            {rts > 0 && `${rts} RT`}{rts > 0 && likes > 0 && ' · '}{likes > 0 && `${likes} ♥`}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <div className="text-gray-300 line-clamp-2 break-words text-[11px] flex-1">
                          {text || '—'}
                        </div>
                        {firstMedia?.url && (
                          <a
                            href={firstMedia.video_url || firstMedia.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 relative"
                          >
                            <img
                              src={firstMedia.url}
                              alt=""
                              className="w-14 h-14 rounded object-cover border border-sky-900/30 hover:border-sky-400/50 transition-colors"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                            />
                            {(firstMedia.type === 'video' || firstMedia.type === 'animated_gif') && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-5 h-5 rounded-full bg-black/60 flex items-center justify-center">
                                  <div className="w-0 h-0 border-l-[6px] border-l-white border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5" />
                                </div>
                              </div>
                            )}
                          </a>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className="text-[11px] font-mono text-gray-500 py-3">
                No tweets yet. Add keywords in Admin → Twitter/X.
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        {showLegend && (
          <div className="absolute bottom-14 right-3 z-10 bg-gray-950/90 backdrop-blur border border-cyan-900/30 rounded px-3 py-2 font-mono">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[9px] text-gray-600 uppercase tracking-[0.15em]">Intel Legend</p>
              <button
                onClick={() => setShowLegend(false)}
                className="text-[9px] text-gray-600 hover:text-green-400 px-1 py-0.5 rounded hover:bg-green-500/10"
                aria-label="Hide legend"
              >
                ✕
              </button>
            </div>
            {colorBy === 'domain' ? (
              <div className="space-y-1">
                {[
                  { label: 'Cyber', color: '#a855f7' },
                  { label: 'Geopolitical', color: '#ef4444' },
                  { label: 'Infrastructure', color: '#f59e0b' },
                ].map((d) => (
                  <div key={d.label} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: d.color, boxShadow: `0 0 6px ${d.color}80` }} />
                    <span className="text-[9px] text-gray-500">{d.label}</span>
                  </div>
                ))}
                <div className="text-[8px] text-gray-700 mt-1 pt-1 border-t border-gray-800/30">Critical = brighter glow</div>
              </div>
            ) : (
              <div className="space-y-1">
                {['critical', 'high', 'medium', 'low'].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shadow-[0_0_6px] ${
                      s === 'critical' ? 'bg-red-500 shadow-red-500/50' :
                      s === 'high' ? 'bg-orange-500 shadow-orange-500/50' :
                      s === 'medium' ? 'bg-yellow-500 shadow-yellow-500/50' :
                      'bg-blue-500 shadow-blue-500/50'
                    }`} />
                    <span className="text-[9px] text-gray-500 capitalize">{s}</span>
                  </div>
                ))}
              </div>
            )}
            {arcs.length > 0 && (
              <div className="border-t border-green-900/40 mt-2 pt-2 space-y-1">
                <p className="text-[9px] text-gray-600 uppercase tracking-[0.15em] mb-1">Attack Vectors</p>
                {[
                  { type: 'Ransomware', color: '#ff0040' },
                  { type: 'Vulnerability', color: '#a855f7' },
                  { type: 'Cyber Attack', color: '#06b6d4' },
                  { type: 'Conflict', color: '#ff6600' },
                ].map((a) => (
                  <div key={a.type} className="flex items-center gap-2">
                    <span className="w-4 h-px rounded" style={{ background: a.color, boxShadow: `0 0 4px ${a.color}80` }} />
                    <span className="text-[9px] text-gray-500">{a.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!showLegend && (
          <button
            onClick={() => setShowLegend(true)}
            className="absolute bottom-14 right-3 z-10 px-2 py-1 text-[9px] font-mono rounded bg-gray-950/95 border border-green-900/50 text-gray-500 hover:text-green-400 hover:border-green-500/60 hover:shadow-[0_0_8px_rgba(74,222,128,0.2)] backdrop-blur"
          >
            LEGEND
          </button>
        )}

        {/* Live status indicator */}
        <div className="absolute bottom-14 left-3 z-10 flex items-center gap-2">
          {wsConnected && (
            <span className="text-[9px] text-emerald-500/80 bg-gray-950/80 backdrop-blur px-2 py-1 rounded border border-emerald-500/20 flex items-center gap-1.5 font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.6)]" />
              LIVE
            </span>
          )}
        </div>
      </div>

      {/* Live event toast notifications */}
      {liveToasts.length > 0 && (
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 z-30 flex flex-col-reverse gap-1.5 items-center pointer-events-none" style={{ width: 380 }}>
          <style>{`@keyframes liveToastIn{0%{opacity:0;transform:translateY(8px) scale(.97)}8%{opacity:1;transform:translateY(0) scale(1)}80%{opacity:1}100%{opacity:0;transform:translateY(-4px) scale(.98)}}`}</style>
          {liveToasts.map((t) => (
            <div
              key={t.id}
              className="w-full bg-gray-950/95 backdrop-blur border border-gray-800 rounded-lg px-3 py-2 shadow-xl"
              style={{
                borderLeftWidth: 3,
                borderLeftColor: t.color,
                animation: `liveToastIn ${(t.expiresAt - Date.now() + 500) / 1000}s ease forwards`,
              }}
            >
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono font-bold uppercase" style={{ color: t.color }}>{t.domain}</span>
                <span className="text-[10px] font-mono text-gray-600">·</span>
                <span className="text-[10px] font-mono uppercase" style={{ color: t.color }}>{t.severity}</span>
                {t.country && <span className="ml-auto text-[10px] font-mono text-gray-500">{t.country}</span>}
              </div>
              <p className="text-[11px] text-gray-200 font-mono mt-0.5 leading-snug line-clamp-2">{t.title}</p>
            </div>
          ))}
        </div>
      )}

      {/* Bottom attack ticker */}
      <AttackTicker events={events} onEventClick={handleEventClick} />

      {/* News ticker footer layer */}
      {newsData && newsData.items.length > 0 && (
        <div className="w-full bg-gray-950 border-t border-green-900/40 overflow-hidden">
          <div className="flex items-center h-7">
            <div className="flex-shrink-0 px-3 py-1 border-r border-green-900/40 flex items-center gap-1.5 z-10 bg-gray-950">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
              <span className="text-[9px] font-bold text-green-400 uppercase tracking-[0.15em] whitespace-nowrap font-mono drop-shadow-[0_0_4px_rgba(74,222,128,0.4)]">
                GLOBAL NEWS
              </span>
            </div>
            <div className="flex-1 overflow-hidden relative">
              <div
                className="flex items-center gap-0 animate-ticker"
                style={{
                  width: 'max-content',
                  animation: `tickerScroll ${Math.max(newsData.items.length * 4, 40)}s linear infinite`,
                }}
              >
                {[...newsData.items.slice(0, 40), ...newsData.items.slice(0, 40)].map((n, i) => (
                  <a
                    key={`${n.link}-${i}`}
                    href={n.link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-0.5 hover:bg-green-500/10 transition-colors border-r border-gray-800/20 group"
                  >
                    <span className="text-[9px] text-gray-500 font-mono">
                      {n.source.slice(0, 14)}
                    </span>
                    <span className="text-[10px] text-gray-300 group-hover:text-gray-50 transition-colors truncate max-w-[260px]">
                      {n.title.length > 80 ? `${n.title.slice(0, 80)}…` : n.title}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Country intel slide-out panel */}
        <CountryIntelPanel
          countryCode={selectedCountry}
          onClose={() => setSelectedCountry(null)}
        />

      {/* Escalation detail modal */}
      {showEscalationDetail && escalation != null && (
        <EscalationDetailPanel
          data={escalation}
          onClose={() => setShowEscalationDetail(false)}
        />
      )}

        <MonitorSearch
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onEventClick={handleEventClick}
          onCountryClick={handleCountryClick}
        />

      {/* Sanctions panel — slide-out when Sanctions layer on */}
      {layers.sanctions && (
        <div className="fixed top-12 left-80 bottom-12 z-30 w-96 bg-gray-950/97 backdrop-blur-md border border-orange-900/30 rounded-xl shadow-2xl overflow-hidden flex flex-col">
          <SanctionsPanel />
        </div>
      )}

      {/* Aircraft panel — slide-out when Flights layer on */}
      {layers.flights && (
        <div className="fixed top-0 right-0 h-full z-30 flex items-center pt-20 pr-2">
          <AircraftDetailPanel
            aircraft={liveFlightsData?.aircraft ?? []}
            selected={selectedAircraft}
            onSelect={(a) => {
              setSelectedAircraft(a)
              if (a && globeRef.current && a.latitude != null && a.longitude != null) {
                globeRef.current.pointOfView({ lat: a.latitude, lng: a.longitude, altitude: 0.5 }, 800)
              }
            }}
            onClose={() => setSelectedAircraft(null)}
          />
        </div>
      )}
    </div>
  )
}

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
